import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Box,
    Typography,
    Paper,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    InputAdornment,
    Radio,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Alert,
    Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler';
import { useStore } from '../../stores';
import { Vehicle } from '../../stores/VehicleStore';

interface MotorcycleSelectorProps {
    onVehicleSelect?: (vehicle: Vehicle | null) => void;
    selectedVehicleId?: number | null;
    maxHeight?: string | number;
}

const MotorcycleSelector: React.FC<MotorcycleSelectorProps> = observer(({
    onVehicleSelect,
    selectedVehicleId = null,
    maxHeight = 500
}) => {
    const { vehicleStore, driverStore } = useStore();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [searchTerm, setSearchTerm] = useState('');
    const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [menuVehicle, setMenuVehicle] = useState<Vehicle | null>(null);

    useEffect(() => {
        vehicleStore.loadVehicles();
        driverStore.loadDrivers();
    }, [vehicleStore, driverStore]);

    const getAvailableVehicles = (): Vehicle[] => {
        const assignedVehicleIds = driverStore.drivers
            .filter(driver => driver.vehicle)
            .map(driver => driver.vehicle);
        
        return vehicleStore.vehicles.filter(vehicle => 
            vehicle.status === 'ACTIVE' && 
            !assignedVehicleIds.includes(vehicle.id)
        );
    };

    useEffect(() => {
        const availableVehicles = getAvailableVehicles();
        
        if (searchTerm.trim() === '') {
            setFilteredVehicles(availableVehicles);
        } else {
            const filtered = availableVehicles.filter(vehicle =>
                vehicle.license_plate.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vehicle.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
                vehicle.model.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredVehicles(filtered);
        }
    }, [searchTerm, vehicleStore.vehicles, driverStore.drivers]);

    const getMaxHeight = () => {
        if (isMobile) return '50vh';
        if (isTablet) return '60vh';
        return maxHeight;
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, vehicle: Vehicle) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setMenuVehicle(vehicle);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuVehicle(null);
    };

    const handleVehicleSelect = (vehicle: Vehicle) => {
        if (onVehicleSelect) {
            // If clicking on already selected vehicle, deselect it
            if (selectedVehicleId === vehicle.id) {
                onVehicleSelect(null);
            } else {
                onVehicleSelect(vehicle);
            }
        }
    };

    const getVehicleStatus = (vehicle: Vehicle) => {
        const isAssigned = driverStore.drivers.some(driver => driver.vehicle === vehicle.id);
        return isAssigned ? 'Asignado' : 'Disponible';
    };

    const menuOptions = [
        { 
            label: 'Ver detalles', 
            action: () => {
                console.log('Ver detalles de vehículo:', menuVehicle?.id);
                handleMenuClose();
            }
        },
        { 
            label: 'Editar vehículo', 
            action: () => {
                console.log('Editar vehículo:', menuVehicle?.id);
                handleMenuClose();
            }
        },
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                borderRadius: 2,
                border: selectedVehicleId ? '2px solid #1976d2' : '1px solid #e0e0e0',
                maxWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative'
            }}
        >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h5" component="h2" fontWeight="500">
                    Seleccionar Vehículo *
                </Typography>
                {selectedVehicleId && (
                    <Chip
                        label="Vehículo Seleccionado"
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 500 }}
                    />
                )}
            </Box>

            <Typography variant="body2" color="text.secondary" mb={3}>
                Selecciona un vehículo disponible para asignar al conductor. Solo se muestran vehículos activos sin conductor asignado.
            </Typography>

            {vehicleStore.error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {vehicleStore.error}
                </Alert>
            )}

            <Box mb={2}>
                <TextField
                    fullWidth
                    placeholder="Buscar por placa, marca o modelo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    disabled={vehicleStore.isLoading}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                    }}
                    variant="outlined"
                    size="small"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            backgroundColor: 'white',
                        }
                    }}
                />
            </Box>

            <Box
                sx={{
                    maxHeight: getMaxHeight(),
                    overflowY: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: '#f1f1f1',
                        borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: '#c1c1c1',
                        borderRadius: '4px',
                        '&:hover': {
                            backgroundColor: '#a8a8a8',
                        },
                    },
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#c1c1c1 #f1f1f1',
                }}
            >
                {vehicleStore.isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary" ml={2}>
                            Cargando vehículos...
                        </Typography>
                    </Box>
                ) : (
                    <List sx={{ p: 0 }}>
                        {filteredVehicles.map((vehicle) => (
                            <ListItem
                                key={vehicle.id}
                                sx={{
                                    borderRadius: 2,
                                    mb: 1,
                                    backgroundColor: selectedVehicleId === vehicle.id ? '#EBF3FF' : 'white',
                                    border: '2px solid',
                                    borderColor: selectedVehicleId === vehicle.id ? '#1976d2' : '#f0f0f0',
                                    '&:hover': {
                                        backgroundColor: selectedVehicleId === vehicle.id ? '#EBF3FF' : '#f9f9f9',
                                        borderColor: selectedVehicleId === vehicle.id ? '#1976d2' : '#dae7fb',
                                    },
                                    p: 2,
                                    cursor: 'pointer'
                                }}
                                onClick={() => handleVehicleSelect(vehicle)}
                                secondaryAction={
                                    <IconButton 
                                        edge="end" 
                                        onClick={(e) => handleMenuOpen(e, vehicle)}
                                        sx={{ 
                                            color: selectedVehicleId === vehicle.id ? '#1976d2' : 'action.default'
                                        }}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                }
                            >
                                <Radio
                                    checked={selectedVehicleId === vehicle.id}
                                    onChange={() => handleVehicleSelect(vehicle)}
                                    onClick={(e) => e.stopPropagation()}
                                    sx={{
                                        p: 0.5,
                                        mr: 2,
                                        color: '#c0c0c0',
                                        '&.Mui-checked': {
                                            color: '#1976d2',
                                        }
                                    }}
                                />
                                <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                                    <TwoWheelerIcon 
                                        sx={{ 
                                            fontSize: 20, 
                                            color: selectedVehicleId === vehicle.id ? '#1976d2' : 'action.active',
                                            mr: 1
                                        }} 
                                    />
                                </Box>
                                <ListItemText
                                    primary={
                                        <Box>
                                            <Typography
                                                variant="body1"
                                                fontWeight={600}
                                                color={selectedVehicleId === vehicle.id ? '#1976d2' : 'text.primary'}
                                            >
                                                {vehicle.license_plate}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color={selectedVehicleId === vehicle.id ? '#1976d2' : 'text.secondary'}
                                                sx={{ mt: 0.5 }}
                                            >
                                                {vehicle.brand} {vehicle.model} • {vehicle.year}
                                            </Typography>
                                        </Box>
                                    }
                                    secondary={
                                        <Box display="flex" alignItems="center" mt={1}>
                                            <Chip
                                                label="Disponible"
                                                color="success"
                                                size="small"
                                                sx={{ 
                                                    fontSize: '0.75rem',
                                                    height: 20,
                                                    fontWeight: 500
                                                }}
                                            />
                                            {vehicle.color && (
                                                <Chip
                                                    label={vehicle.color}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ 
                                                        ml: 1,
                                                        fontSize: '0.75rem',
                                                        height: 20
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                )}

                {!vehicleStore.isLoading && filteredVehicles.length === 0 && (
                    <Box textAlign="center" py={6}>
                        <TwoWheelerIcon 
                            sx={{ 
                                fontSize: 48, 
                                color: 'text.disabled',
                                mb: 2
                            }} 
                        />
                        <Typography variant="h6" color="text.secondary" mb={1}>
                            {searchTerm ? 
                                `No se encontraron vehículos con "${searchTerm}"` :
                                'No hay vehículos disponibles'
                            }
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {searchTerm ? 
                                'Intenta con otros términos de búsqueda' :
                                'Todos los vehículos están asignados o inactivos'
                            }
                        </Typography>
                    </Box>
                )}
            </Box>

            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                        borderRadius: 2,
                        minWidth: 180,
                    }
                }}
            >
                {menuOptions.map((option, index) => (
                    <MenuItem
                        key={index}
                        onClick={option.action}
                        sx={{ py: 1.5 }}
                    >
                        <Typography variant="body2">
                            {option.label}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </Paper>
    );
});

export default MotorcycleSelector;
